import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { DefaultButton, PaginationTable, RowLink } from 'common/components';
import { withTheme } from 'common/styling/theme';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import SmallAreaChart from '../SmallAreaChart';

import { REWARD_TYPES_TR_KEYS } from '../../consts';

import { staticStyles, getDynamicStyles } from './style';

const drawTradersFee = fee => (REWARD_TYPES_TR_KEYS[fee] ? <FormattedMessage id={REWARD_TYPES_TR_KEYS[fee]} /> : '一');

const getFixedNumber = (num, decimal) => Number(Number(num).toFixed(decimal));
const toPercentage = num => (!Number.isNaN(num) ? `${getFixedNumber(num * 100, 2)}%` : '一');

const InvestRatingTable = ({
  data,
  onRowClick,
  toggleInvestModal,
  loading,
  getList,
  location,
  history,
  params,
  theme,
  ...props
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const handleInvest = (event, ...values) => {
    event.stopPropagation();

    toggleInvestModal(...values);
  };
  const justInvestNew = 'justInvest';

  /* eslint-disable */
  const columns = useMemo(
    () => [
      {
        accessor: 'name',
        Header: 'justStrategy',
        Cell: ({
          row: {
            original: { title, symbols, id },
          },
        }) => (
          <RowLink isPreviewCell link={`/investNew/${id}`}>
            <div className="InvestRatingTable__strategy">
              <span className="InvestRatingTable__strategy-caption">{title}</span>
              <span className="InvestRatingTable__strategy-symbols">{symbols}</span>
            </div>
          </RowLink>
        ),
        isPreviewCell: true,
      },
      {
        accessor: 'total_yield',
        Header: 'investIncomeMonthlyAndTotal',
        Cell: ({
          row: {
            original: { monthlyYield, totalYield, id },
          },
        }) => (
          <RowLink isPreviewCell link={`/investNew/${id}`}>
            <div className="InvestRatingTable__yield">
              <span className="InvestRatingTable__monthlyYield">{toPercentage(monthlyYield || 0)}/</span>
              <span className="InvestRatingTable__totalYield">{toPercentage(totalYield)}</span>
            </div>
          </RowLink>
        ),
        isPreviewCell: true,
      },
      {
        Header: 'investBalanceOperations',
        Cell: ({
          row: {
            original: {
              settings: { autoInvestment },
              rollovers,
              id,
            },
          },
        }) => {
          if (autoInvestment && !rollovers)
            return (
              <RowLink link={`/investNew/${id}`}>
                <FormattedMessage id="investAuto" />
              </RowLink>
            );
          if (autoInvestment && rollovers)
            return (
              <RowLink link={`/investNew/${id}`}>
                <FormattedMessage id="investRollover" />
              </RowLink>
            );
          if (!autoInvestment && !rollovers)
            return (
              <RowLink link={`/investNew/${id}`}>
                <FormattedMessage id="justManual" />
              </RowLink>
            );
          if (!autoInvestment && rollovers)
            return (
              <RowLink link={`/investNew/${id}`}>
                <FormattedMessage id="investManualRollover" />
              </RowLink>
            );
        },
      },
      {
        accessor: 'settings__reward_type',
        Header: 'investTradersFee',
        Cell: ({
          row: {
            original: { fee, id },
          },
        }) => <RowLink link={`/investNew/${id}`}>{drawTradersFee(fee)}</RowLink>,
      },
      {
        accessor: 'settings__reward_percentage',
        Header: 'justFeeRate',
        Cell: ({
          row: {
            original: { feeRate, id },
          },
        }) => (
          <RowLink link={`/investNew/${id}`}>
            <span>{feeRate}%</span>
          </RowLink>
        ),
      },
      {
        Header: 'justDynamics',
        Cell: ({
          row: {
            original: { chart = [] },
          },
        }) => {
          const chartData = chart.length ? chart.map(item => Math.round(item * 100) || item * 100) : Array(10).fill(0);
          return <SmallAreaChart seriesData={chartData} categoriesData={[...Array(chartData.length).keys()]} />;
        },
      },
      {
        accessor: 'investors_number',
        Header: 'justInvestorsNumber',
        Cell: ({
          row: {
            original: { investorsNumber, id },
          },
        }) => (
          <RowLink link={`/investNew/${id}`}>
            <span>{investorsNumber ?? '一'}</span>
          </RowLink>
        ),
      },
      {
        accessor: 'age',
        Header: 'ageAndWeeks',
        Cell: ({
          row: {
            original: { age, id },
          },
        }) => (
          <RowLink link={`/investNew/${id}`}>
            <span>{age}</span>
          </RowLink>
        ),
      },
      {
        accessor: 'equity',
        Header: 'justAUM',
        Cell: ({
          row: {
            original: { equity },
          },
        }) => (
          <span>
            <FormattedNumber value={equity.amount} maximumFractionDigits={8} />
            {` ${equity.currency}`}
          </span>
        ),
      },
      {
        Header: () => null,
        id: 'actions',
        Cell: ({
          row: {
            original: {
              account,
              id,
              settings: { minInvestmentAmount },
            },
          },
        }) => (
          <DefaultButton
            textId={justInvestNew}
            onClick={e => handleInvest(e, id, account.asset, account.id, minInvestmentAmount)}
            filled
          />
        ),
      },
    ],
    []
  );
  /* eslint-enable */

  return (
    <div className="InvestRatingTable">
      <PaginationTable
        {...props}
        data={data}
        columns={columns}
        onRowClick={onRowClick}
        loading={loading}
        getList={getList}
        location={location}
        history={history}
        params={params}
      />
      <style jsx>{staticStyles}</style>
      <style jsx>{dynamicStyles}</style>
    </div>
  );
};

InvestRatingTable.propTypes = {
  data: PropTypes.object.isRequired,
  onRowClick: PropTypes.func.isRequired,
  toggleInvestModal: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  getList: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  theme: PropTypes.object,
};

InvestRatingTable.defaultProps = {
  theme: {},
};

export default withTheme()(InvestRatingTable);
